import React from "react";
import { CalendarCheck2, Fish, Library, MapPin, Trophy, Waves } from "lucide-react";
import { useTranslation } from "react-i18next";

const GEAR_ICON_PATHS = {
  hook: "/icons/gear/hook.png",
  leader: "/icons/gear/leader.png",
  "line-braid": "/icons/gear/line-braid.png",
  reel: "/icons/gear/reel.png",
  rod: "/icons/gear/rod.png",
};

function RecordedRows({ rows, prefix = "" }) {
  return (
    <div className="mytrips-past-recorded-rows">
      {rows.map((row) => (
        <div
          key={`${prefix}-${row.type || "value"}-${row.value}`}
          className="mytrips-past-recorded-row"
        >
          <span>{row.value}</span>
          <strong>{row.count}</strong>
        </div>
      ))}
    </div>
  );
}

export function PastTripResultCards({ pastSummary }) {
  const { t } = useTranslation();
  const biggestCatch = pastSummary.biggestRecordedCatch;
  const cards = [
    {
      key: "catches",
      Icon: Library,
      label: t("trip_report_total_catches"),
      value: pastSummary.totalRecordedCatches,
    },
    {
      key: "species",
      Icon: Fish,
      label: t("trip_report_species_caught"),
      value: pastSummary.distinctSpeciesCount,
    },
    {
      key: "biggest",
      Icon: Trophy,
      label: t("trip_report_biggest_recorded"),
      value: biggestCatch?.weight || t("trip_report_not_recorded"),
      detail: biggestCatch?.fish || "",
    },
    {
      key: "days",
      Icon: CalendarCheck2,
      label: t("trip_report_active_days"),
      value: pastSummary.activeCatchDays,
    },
  ];

  return (
    <section className="mytrips-past-mobile-only mytrips-past-result-section">
      <h4 className="mytrips-past-summary-section-title">
        {t("trip_report_results_title")}
      </h4>
      <div className="mytrips-past-result-grid">
        {cards.map(({ key, Icon, label, value, detail }) => (
          <div key={key} className="mytrips-past-result-card">
            <span className="mytrips-past-result-icon" aria-hidden="true">
              <Icon size={18} strokeWidth={2.3} />
            </span>
            <span className="mytrips-past-result-copy">
              <span>{label}</span>
              <strong>{value}</strong>
              {detail ? <small>{detail}</small> : null}
            </span>
          </div>
        ))}
      </div>
      <p className="mytrips-past-result-source-note">
        {t("trip_report_source_summary", {
          autoCount: pastSummary.autoCollectedCount,
          manualCount: pastSummary.manualCatchCount,
        })}
      </p>
    </section>
  );
}

export function PastTripRecordedBreakdowns({ pastSummary }) {
  const { t } = useTranslation();
  const hasRecordedSetup = pastSummary.recordedSetupGroups.length > 0;

  return (
    <>
      <section className="mytrips-past-mobile-only mytrips-past-breakdown-section">
        <div className="mytrips-past-section-heading">
          <img src="/icons/gear/gear-set.png" alt="" aria-hidden="true" />
          <div>
            <h4 className="mytrips-past-summary-section-title">
              {t("trip_report_gear_used")}
            </h4>
            <p>{t("trip_report_recorded_setup_note")}</p>
          </div>
        </div>

        {hasRecordedSetup ? (
          <div className="mytrips-past-setup-grid">
            {pastSummary.recordedSetupGroups.map((group) => (
              <div key={group.key} className="mytrips-past-setup-card">
                <div className="mytrips-past-setup-card-head">
                  <img src={GEAR_ICON_PATHS[group.icon]} alt="" aria-hidden="true" />
                  <strong>{t(`trip_report_setup_${group.key}`)}</strong>
                </div>
                <RecordedRows rows={group.rows} prefix={group.key} />
              </div>
            ))}
          </div>
        ) : (
          <p className="mytrips-past-summary-empty">
            {t("trip_report_no_setup_data")}
          </p>
        )}
      </section>

      {pastSummary.lureAndBaitRows.length > 0 ? (
        <section className="mytrips-past-mobile-only mytrips-past-breakdown-section">
          <div className="mytrips-past-section-heading">
            <img src="/icons/gear/lure.png" alt="" aria-hidden="true" />
            <div>
              <h4 className="mytrips-past-summary-section-title">
                {t("trip_report_lures_bait")}
              </h4>
              <p>{t("trip_report_recorded_catches_note")}</p>
            </div>
          </div>
          <RecordedRows rows={pastSummary.lureAndBaitRows} prefix="lure-bait" />
        </section>
      ) : null}

      {pastSummary.recordedMethodGroups.length > 0 ? (
        <section className="mytrips-past-mobile-only mytrips-past-breakdown-section">
          <div className="mytrips-past-section-heading mytrips-past-section-heading--lucide">
            <span aria-hidden="true">
              <Waves size={18} strokeWidth={2.3} />
            </span>
            <div>
              <h4 className="mytrips-past-summary-section-title">
                {t("trip_report_methods_recorded")}
              </h4>
              <p>{t("trip_report_recorded_catches_note")}</p>
            </div>
          </div>
          <div className="mytrips-past-setup-grid">
            {pastSummary.recordedMethodGroups.map((group) => (
              <div key={group.key} className="mytrips-past-setup-card">
                <strong>{t(`trip_report_method_${group.key}`)}</strong>
                <RecordedRows rows={group.rows} prefix={group.key} />
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {pastSummary.recordedLocationRows.length > 0 ? (
        <section className="mytrips-past-mobile-only mytrips-past-breakdown-section">
          <div className="mytrips-past-section-heading mytrips-past-section-heading--lucide">
            <span aria-hidden="true">
              <MapPin size={18} strokeWidth={2.3} />
            </span>
            <div>
              <h4 className="mytrips-past-summary-section-title">
                {t("trip_report_locations_recorded")}
              </h4>
              <p>{t("trip_report_location_privacy_note")}</p>
            </div>
          </div>
          <RecordedRows rows={pastSummary.recordedLocationRows} prefix="location" />
        </section>
      ) : null}
    </>
  );
}

export function PastTripMemories({ trip }) {
  const { t } = useTranslation();
  if (!trip?.notes) return null;

  return (
    <section className="mytrips-past-mobile-only mytrips-past-memories-section">
      <h4 className="mytrips-past-summary-section-title">
        {t("trip_report_memories_notes")}
      </h4>
      <p>{trip.notes}</p>
    </section>
  );
}
